import type {
  BeforeUploadType,
  UploadFileValidatorProps,
} from "./upload-file-validator.type";

import { Upload } from "antd";
import { useTranslation } from "react-i18next";

import { useAppNotification } from "@/modules/app-notification";
import { fileNameIsTooLong } from "@/utilities/file";

import UploadFileTypesInfo from "../upload-file-types-info";

import {
  isValidFileSize,
  isValidFileToUpload,
} from "./upload-file-validator.helper";

export const useUploadFileValidator = ({
  authorizedFileTypes,
  maxFileSize,
  onBeforeUpload,
}: UploadFileValidatorProps) => {
  const { t } = useTranslation();
  const { notify } = useAppNotification();

  const beforeUpload: BeforeUploadType = (file) => {
    // 1 - Validate file format
    if (!isValidFileToUpload({ file, authorizedFileTypes })) {
      notify.error({
        title: t("global:upload.error.fileType"),
        description: (
          <UploadFileTypesInfo
            authorizedFileTypes={authorizedFileTypes}
            asSentence
          />
        ),
      });
      return Upload.LIST_IGNORE;
    }

    // 2 - Validate file size
    if (!isValidFileSize(file, maxFileSize)) {
      notify.error({
        title: t("global:upload.maxFileSize", { maxFileSize }),
      });
      return Upload.LIST_IGNORE;
    }

    // 3 - Validate file name length
    if (fileNameIsTooLong(file)) {
      notify.error({
        title: t("global:upload.error.nameLength", { fileName: file.name }),
      });
      return Upload.LIST_IGNORE;
    }

    // 4 - If custom user validation, return it
    if (onBeforeUpload) return onBeforeUpload(file);

    // Otherwise everything is fine!
    return true;
  };

  return {
    beforeUpload,
  };
};
